import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MbPopoverTrigger, MbIconTextModule, MbIconModule } from '@am/moebius';
import { ReqService, SearchStateService } from '@core/services';
import { RoomOptions } from '../room-options.class';
import { IHotelAvailability } from '@core/models/HotelAvailability/IHotelAvailability';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.reducer';
import {
  selectAreThereMoreRooms,
  selectFilteredRooms,
} from '@store/hotel-availability/hotel-availability.selectors';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { DisneyClusterDisneyComponent } from '../../../disney/disney-cluster/disney-cluster.component';
import { DetailClusterComponent } from '../../detail-cluster/detail-cluster.component';
import { NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';
import { AlertContentComponent } from '@commons/alert-content/alert-content.component';

@Component({
    selector: 'room-options',
    templateUrl: './room-options.component.html',
    styleUrls: ['./room-options.component.scss'],
    standalone: true,
    imports: [
        AlertContentComponent,
        NgIf,
        NgFor,
        DetailClusterComponent,
        NgClass,
        DisneyClusterDisneyComponent,
        MbIconTextModule,
        MbIconModule,
        AsyncPipe,
    ],
})
export class RoomOptionsComponent extends RoomOptions implements OnInit {
  @ViewChild(MbPopoverTrigger, { static: true }) popover: MbPopoverTrigger;

  @Input()
  paymentMethodValue = '';
  @Input()
  hotelAvailabilityInfo: IHotelAvailability = {} as never;
  @Input()
  priceByValue: string;
  @Input()
  showAmountTotal: boolean;
  @Input()
  isDisney = false;
  @Input()
  hotelStaticInfo: any;
  @Output()
  paymentMethodSelected: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  handleRefundableRateChange: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  showMoreRooms: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input()
  isHomestay: boolean;
  public rooms$: Observable<any[]>; // ToDo: set Type for Rooms
  public areThereMoreRooms$: Observable<boolean>;

  @Input()
  set showPaymentsOptionsAlert(_shouldShowAlert: any) {
    if (this.openRefundableRateAlert) {
      setTimeout(() => {
        this.openPaymentsMethodsAlert = _shouldShowAlert;
      }, 5000);
    } else {
      this.openPaymentsMethodsAlert = _shouldShowAlert;
    }
  }

  @Input()
  set showRefundableRateAlert(_shouldShowAlert: any) {
    if (_shouldShowAlert) {
      this.show();
    } else {
      this.hide();
    }
  }

  constructor(
    protected searchStateService: SearchStateService,
    protected cdr: ChangeDetectorRef,
    protected reqService: ReqService,
    private readonly store: Store<AppState>,
  ) {
    super(searchStateService, reqService, cdr);
    this.rooms$ = this.store
      .select(selectFilteredRooms)
      .pipe(tap(roomClusters => (this.showAllBtn = roomClusters.length > 0)));
    this.areThereMoreRooms$ = this.store.select(selectAreThereMoreRooms);
  }

  ngOnInit() {
    super.ngOnInit();
  }

  // Popover display functions
  private show() {
    this.openRefundableRateAlert = true;
    if (this.popover) {
      this.popover.show();
    }
  }

  public hide() {
    this.openRefundableRateAlert = false;
    if (this.popover) {
      this.popover.hide();
    }
  }

  /**
   * Listen changes on showRefundableRateAlert
   */
  get showRefundableRateAlert(): any {
    return this.openRefundableRateAlert;
  }

  /**
   * Listen changes on showPaymentsOptionsAlert
   */
  get showPaymentsOptionsAlert(): any {
    return this.openPaymentsMethodsAlert;
  }

  /**
   * Toggles tabs to show the different payment method
   */
  public paymentMethodChange(data) {
    this.paymentMethodValue = data;
    this.paymentMethodSelected.emit(this.paymentMethodValue);
    this.showRooms();
  }

  public trackByItems(index, _item) {
    return index;
  }

  /**
   * Toggles refundable rate switch
   */
  public refundableRateChange(data: boolean) {
    this.refundableRate = data;
    this.handleRefundableRateChange.emit(this.refundableRate);
    this.showRooms();
  }

  filterMealPLans() {
    if (!this.mealPlan || this.mealPlan === 'ALL') {
      this.paidRooms = this.allPaidRooms;
      this.createDisneyRooms();
    } else {
      if (this.isDisney) {
        this.paidRooms = this.filterRoomsDisney().filter((paidRoom) => {
          return paidRoom.disney_meal_plans[0].rates.length > 0;
        });
        this.createDisneyRooms();
      } else {
        this.paidRooms = this.allPaidRooms
          .map((paidRoom) => {
            const rates = paidRoom.rates.filter((rate) => {
              return rate.meal_plan.code === this.mealPlan;
            });
            return { ...paidRoom, rates };
          })
          .filter((paidRoom) => paidRoom.rates.length > 0);
      }
    }
    if (this.paidRooms.length === 0) {
      this.showRooms();
    }
    this.cdr.detectChanges();
  }

  filterRoomsDisney(): any[] {
    switch (this.mealPlan) {
      case 'ROOM_ONLY': {
        return this.allPaidRooms.filter((paidRoom) => {
          return paidRoom.disney_meal_plans[0].code === this.mealPlan;
        });
      }
      case 'MEAL_PLAN': {
        return this.allPaidRooms.filter((paidRoom) => {
          return (
            this.splitNameMealPLan(paidRoom.rooms[0].room_description.description) !==
            undefined
          );
        });
      }
      case 'TICKET': {
        return this.allPaidRooms.filter((paidRoom) => {
          return paidRoom.disney_meal_plans[0].rates.length > 1;
        });
      }
      default: {
        return this.allPaidRooms.filter((paidRoom) => {
          return (
            this.mealPlan ===
            this.splitNameMealPLan(paidRoom.rooms[0].room_description.description)
          );
        });
      }
    }
  }

  hasClusterWithTicket(): boolean {
    const roomsWithTicket = this.allPaidRooms.filter((paidRoom) => {
      return paidRoom.disney_meal_plans[0].rates.length > 1;
    });
    return roomsWithTicket.length > 0;
  }

  hasMealPlanFilter(): boolean {
    if (this.mealPlanFilter.length > 0) {
      return (
        this.mealPlanFilter.find((mealPlan) => {
          return mealPlan.code !== 'ROOM_ONLY';
        }) !== undefined
      );
    } else {
      return false;
    }
  }

  public showRooms(): void {
    this.showMoreRooms.emit(true);
  }
}
